/**
 * 统一的语言配置文件
 * 所有语言相关的配置都从这里导出，避免多处重复声明
 */

// 完整的语言列表配置
export const LANGUAGE_LIST = [
  {
    name: 'English',
    key: 'en',
    shortName: 'EN'
  },
  // 哈萨克语
  {
    name: 'Русский',
    key: 'kk',
    shortName: 'ҚАЗ'
  },
  // 西班牙语
  {
    name: 'Español',
    key: 'es',
    shortName: 'ES'
  },
  // 法语
  {
    name: 'Français',
    key: 'fr',
    shortName: 'FR'
  },
  // 德语
  {
    name: 'Deutsch',
    key: 'de',
    shortName: 'DE'
  },
  // 荷兰语
  {
    name: 'Nederlands',
    key: 'nl',
    shortName: 'NL'
  },
  // 匈牙利语
  {
    name: 'Magyar',
    key: 'hu',
    shortName: 'HU'
  },
  // 芬兰语
  {
    name: 'Suomi',
    key: 'fi',
    shortName: 'FI'
  },
  // 挪威语
  {
    name: 'Norsk',
    key: 'no',
    shortName: 'NO'
  },
  // 意大利语
  {
    name: 'Italiano',
    key: 'it',
    shortName: 'IT'
  },
  // 葡萄牙语
  {
    name: 'Português',
    key: 'pt',
    shortName: 'PT'
  },
  // 俄语
  {
    name: 'Pусский',
    key: 'ru',
    shortName: 'РУС'
  },
  // 土耳其语
  {
    name: 'Türkçe',
    key: 'tr',
    shortName: 'TR'
  },
  // 韩语
  {
    name: '한국어',
    key: 'ko',
    shortName: '한국'
  },
  // 日语
  {
    name: '日本語',
    key: 'ja',
    shortName: '日本'
  },
  // 越南语
  {
    name: 'Tiếng Việt',
    key: 'vi',
    shortName: 'VN'
  },
  // 印尼语
  {
    name: 'Bahasa Indonesia',
    key: 'id',
    shortName: 'ID'
  },
  // 马来语
  {
    name: 'Bahasa Melayu',
    key: 'ms',
    shortName: 'MY'
  },
  // 泰语
  {
    name: 'ภาษาไทย',
    key: 'th',
    shortName: 'ไทย'
  },
  // 阿拉伯语
  {
    name: 'العربية',
    key: 'ar',
    shortName: 'عر'
  },
  // 希伯来语
  {
    name: 'עברית',
    key: 'he',
    shortName: 'עב'
  },
  // 印地语
  {
    name: 'हिन्दी',
    key: 'hi',
    shortName: 'हिं'
  },
  // 波兰语
  {
    name: 'Polski',
    key: 'pl',
    shortName: 'PL'
  },
  // 斯洛伐克语
  {
    name: 'Slovenský',
    key: 'sk',
    shortName: 'SK'
  },
  // 捷克语
  {
    name: 'Čeština',
    key: 'cs',
    shortName: 'CZ'
  },
  // 简体中文
  {
    name: '简体中文',
    key: 'zh-CN',
    shortName: '中文'
  },
  // 繁体中文
  {
    name: '繁体中文',
    key: 'zh-TW',
    shortName: '繁體'
  }
]

// 支持的语言代码列表（用于默认语言判断）
export const SUPPORTED_LANGUAGES = LANGUAGE_LIST.map(lang => lang.key)

// 语言键值对映射（key -> name）
export const LANGUAGE_MAP = LANGUAGE_LIST.reduce((map, lang) => {
  map[lang.key] = lang.name
  return map
}, {})

// 语言短名称映射（key -> shortName）
export const LANGUAGE_SHORT_MAP = LANGUAGE_LIST.reduce((map, lang) => {
  map[lang.key] = lang.shortName
  return map
}, {})

/**
 * 根据语言key获取语言对象
 * @param {string} key - 语言代码
 * @returns {object|null} 语言对象
 */
export function getLanguageByKey(key) {
  return LANGUAGE_LIST.find(lang => lang.key === key) || null
}

/**
 * 根据语言key获取语言名称
 * @param {string} key - 语言代码
 * @returns {string} 语言名称
 */
export function getLanguageName(key) {
  const lang = getLanguageByKey(key)
  return lang ? lang.name : ''
}

/**
 * 根据语言key获取语言短名称
 * @param {string} key - 语言代码
 * @returns {string} 语言短名称
 */
export function getLanguageShortName(key) {
  const lang = getLanguageByKey(key)
  return lang ? lang.shortName : 'EN'
}

/**
 * 获取默认语言
 * 优先使用用户保存的语言，否则根据浏览器语言自动适配
 * @returns {string} 语言代码
 */
export function getDefaultLanguage() {
  // 如果用户手动设置过语言，优先使用保存的语言
  const savedLanguage = localStorage.getItem('locale')
  if (savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage)) {
    return savedLanguage
  }
  
  // 否则根据系统语言自动适配
  const language = navigator.language
  const clientLanguage = (language !== 'zh-CN' && language !== 'zh-TW') 
    ? language.substring(0, 2) 
    : language
  
  // 如果系统语言在支持列表中，使用系统语言，否则默认使用英文
  return SUPPORTED_LANGUAGES.includes(clientLanguage) ? clientLanguage : 'en'
}

/**
 * 动态加载所有语言包
 * 用于 vue-i18n 的 messages 配置
 * @returns {object} 语言包对象
 */
export function loadLanguageMessages() {
  const messages = {}
  
  LANGUAGE_LIST.forEach(lang => {
    try {
      // 动态 require 语言包文件
      messages[lang.key] = require(`./lang/${lang.key}.yaml`)
    } catch (error) {
      console.error(`Failed to load language pack: ${lang.key}`, error)
    }
  })
  
  return messages
}
